import React, { useEffect, useState } from "react";
import { getAuditLogs } from "../api/client.js";

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
}

const ACTION_COLORS = {
  CREATE: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200",
  UPDATE: "border-sky-400/40 bg-sky-500/10 text-sky-200",
  DELETE: "border-rose-400/40 bg-rose-500/10 text-rose-200",
  LOGIN: "border-purple-400/40 bg-purple-500/10 text-purple-200",
  APPROVE: "border-lime-400/40 bg-lime-500/10 text-lime-200",
  REJECT: "border-orange-400/40 bg-orange-500/10 text-orange-200"
};

export default function CrmAuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    userId: "",
    action: "",
    entity: "",
    start: "",
    end: "",
    page: 1,
    pageSize: 50
  });

  const load = () => {
    setLoading(true);
    setError("");
    getAuditLogs(filters)
      .then((response) => {
        const data = response?.data ?? response?.logs ?? response ?? [];
        setLogs(Array.isArray(data) ? data : []);
      })
      .catch((err) => setError(err.message || "Unable to load audit logs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Compliance monitoring</p>
        <h1 className="text-3xl font-semibold text-white">Audit Log</h1>
        <p className="text-sm text-white/70">
          All system actions are logged for compliance and security auditing.
        </p>
      </header>

      <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400 mb-3">Filters</p>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="text-xs text-white/70">
            User ID
            <input
              className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
              value={filters.userId}
              onChange={(event) => handleFilterChange("userId", event.target.value)}
              placeholder="Filter by user ID"
            />
          </label>
          <label className="text-xs text-white/70">
            Action
            <select
              className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
              value={filters.action}
              onChange={(event) => handleFilterChange("action", event.target.value)}
            >
              <option value="">All actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="LOGIN">Login</option>
              <option value="APPROVE">Approve</option>
              <option value="REJECT">Reject</option>
            </select>
          </label>
          <label className="text-xs text-white/70">
            Entity
            <input
              className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
              value={filters.entity}
              onChange={(event) => handleFilterChange("entity", event.target.value)}
              placeholder="Listing, Contact, Offer..."
            />
          </label>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="text-xs text-white/70">
            Start date
            <input
              type="date"
              className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
              value={filters.start}
              onChange={(event) => handleFilterChange("start", event.target.value)}
            />
          </label>
          <label className="text-xs text-white/70">
            End date
            <input
              type="date"
              className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
              value={filters.end}
              onChange={(event) => handleFilterChange("end", event.target.value)}
            />
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() =>
              setFilters({
                userId: "",
                action: "",
                entity: "",
                start: "",
                end: "",
                page: 1,
                pageSize: 50
              })
            }
            className="rounded-xl border border-white/15 bg-black/40 px-4 py-2 text-xs text-white hover:bg-white/10"
          >
            Clear filters
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-sm text-white/60">
          Loading audit logs…
        </div>
      ) : logs.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-sm text-white/60">
          No audit logs match your filters.
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => {
            const actionColor = ACTION_COLORS[log.action] || "border-white/20 bg-white/10 text-white/80";
            return (
              <div
                key={log.id}
                className="rounded-2xl border border-white/10 bg-white/5 p-4 flex flex-wrap items-start gap-4"
              >
                <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-wider ${actionColor}`}>
                  {log.action}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium">
                    {log.entity} {log.entityId ? `#${log.entityId}` : ""}
                  </p>
                  {log.details && (
                    <p className="text-xs text-white/60 mt-1 truncate">{JSON.stringify(log.details)}</p>
                  )}
                  <p className="text-[10px] text-white/50 mt-1">
                    User: {log.userId || "—"} • {formatDate(log.createdAt)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {logs.length > 0 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
            disabled={filters.page === 1}
            className="rounded-xl border border-white/15 bg-black/40 px-4 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs text-white">
            Page {filters.page}
          </span>
          <button
            onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
            disabled={logs.length < filters.pageSize}
            className="rounded-xl border border-white/15 bg-black/40 px-4 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
